'use client';

import { useEffect, useState, useContext, type FC } from 'react';
import { Alert, Dialog, Flex, Icon, Loading, Text } from '@/_components';
import { saveQuestionnaire } from '@/_api/client-questionnaires';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { Questionnaire } from '@/_types/models/questionnaire';
import styles from './SelectFirmInternalStatusDialog.module.css';
import type { SelectFirmInternalStatusDialogProps } from './SelectFirmInternalStatusDialog.types';

export const SelectFirmInternalStatusDialog: FC<
  SelectFirmInternalStatusDialogProps
> = ({
  isOpen,
  loadQuestionnaires,
  onClose,
  selectedQuestionnaires,
  title,
}) => {
  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);
  const { firm } = useContext(FirmSettingsContext);

  const [isLoading, setIsLoading] = useState(false);

  // when unmounted, reset all state:
  useEffect(() => {
    return () => {
      setIsLoading(false);
    };
  }, []);

  const onStatusChoose = async (statusId: number) => {
    try {
      setIsLoading(true);

      const promises: Array<any> = [];

      selectedQuestionnaires.forEach((questionnaire: Questionnaire) => {
        promises.push(
          async () =>
            await saveQuestionnaire({
              id: questionnaire?.id,
              companyId: questionnaire?.company_id,
              params: {
                firm_custom_status_id: statusId,
              },
              token,
            }),
        );
      });

      await Promise.all(promises.map((p) => p()));
      await loadQuestionnaires();

      addToast(
        'success',
        'Firm custom statuses of questionnaire(s) successfully updated',
      );
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  return (
    <Dialog isNarrow={true} isOpen={isOpen} onClose={onClose} title={title}>
      <Dialog.Content>
        <Alert type="info" icon="info">
          Please select Firm-Internal Status. The client instruction is listed
          below.
        </Alert>

        <Flex gap={10} isColumn>
          {isLoading ? (
            <Loading size={20} />
          ) : (
            <>
              {firm?.firm_custom_statuses?.map((status) => (
                <div
                  className={styles.statusContainer}
                  key={status.id}
                  onClick={() => onStatusChoose(status.id)}
                >
                  <Flex gap={2}>
                    <Text color="accent">
                      {status.firm_user_display_status}
                    </Text>
                    <Icon color="accent" name="arrow-right" size={16} />
                  </Flex>

                  <Text color="secondary" type="tiny">
                    {status.client_display_status}
                  </Text>
                </div>
              ))}
            </>
          )}
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
